// The opossum import should not be bundled in the browser until we wish to
// support circuit breakers in the browser. To achieve this, it has been added
// to the exclusion list in the "browser" field of package.json.

import opossum from 'opossum';

const DEFAULT_STATUS_INTERVAL = 1000;

// An implementation of the circuit breaker pattern, tailored for use with Apollo/GraphQL:
// https://martinfowler.com/bliki/CircuitBreaker.html
//
// Wraps a fetch() function to enhance it with circuit breaking capability. The
// circuit will open when the errorThresholdPercentage is exceeded during the
// last rollingCountTimeout window. Timeouts and network errors influence the
// opening of the circuit. All other outcomes, do not influence the circuit.
//
// The intention of this circuit breaker is to add resiliency to our upstream
// systems by respecting backpressure they supply. If an upstream endpoint is
// struggling, as indicated by timing out, network failures, etc., we want to
// open the circuit and fail fast for a short time period, to relieve pressure
// on that endpoint. By failing fast, we can help to mitigate production
// outages, while providing a better experience for the user (who will not need
// to wait 30 seconds only to see their request timeout request to timeout).
// The mitigation of cascade failures is also valuable.
//
// Options:
//
// allowWarmUp (boolean)
// Allow failures without opening the circuit during a brief warmup period.
//
// capacity (number)
// Maximum number of concurrent requests before opening the circuit.
//
// errorThresholdPercentage (number)
// The integer percentage (e.g. 50) of requests which must fail within the
// rollingCountTimeout to open the circuit.
//
// isPassive (boolean)
// If true, allows the circuit breaker to function normally, except that when
// the circuit is open, no requests will be rejected. Useful for testing
// statistics collection in production without actually tripping the breaker.
//
// onStatus (function)
// Called every statusInterval to report the current status of the circuit
// breaker. Receives a status object argument describing the current circuit
// breaker state.
//
// resetTimeout (number)
// After the circuit is opened, amount of milliseconds to wait before entering
// half-open state and potentially closing the circuit.
//
// rollingCountBuckets (number)
// Sets the number of buckets the statistical rolling window is divided into.
//
// rollingCountTimeout (number)
// Duration of the statistical rolling window, in milliseconds.
//
// statusInterval (number)
// Time interval in milliseconds at which to repeatedly call onStatus callback
// function.
//
// isBrowser (boolean)
// Indicate whether or not the circuit breaker is currently operating in a
// browser or server environment.
//
// timeout (number)
// Requests taking longer than this number of milliseconds will be treated as
// failures.
//
// volumeThreshold (number)
// The number of requests which must be exceeded within the most recent
// rollingCountTimeout statistical window, before the circuit will open.
const circuitBreaker = (fetchFunction, options) => {
  const {
    isBrowser,
    isPassive,
    statusInterval,
    volumeThreshold = 0,
    ...opossumOptions
  } = options;

  // Circuit breaker should be disabled in the browser, as we do not have
  // enough request volume on the client-side to reliably trip the breaker at
  // the appropriate time. This decision can be revisited if we can devise an
  // algorithm which will eliminate false negatives.
  if (isBrowser) {
    return fetchFunction;
  }

  let state = 'close';
  const setState = newState => () => (state = newState);
  const breaker = opossum(fetchFunction, opossumOptions);

  // Records the current state of the circuit breaker, for the later submission
  // of status reports to interested consumers.
  breaker.on('halfOpen', setState('halfOpen'));
  breaker.on('close', setState('close'));

  breaker.fallback((...args) => {
    const error = args[args.length - 1];

    // The fallback function is fired after _any_ error. So if the current call
    // is not due to an open breaker, we want to avoid calling fetchFunction()
    // a second time, which would effectively retry the failed request.
    if (error !== 'Breaker is open') {
      return Promise.reject(error);
    }

    const insufficientVolume = breaker.stats.fires <= volumeThreshold;

    // If there is insufficient volume to open the breaker, even though
    // opossum's internal state is 'open', this request should not be rejected.
    if (insufficientVolume) {
      return fetchFunction(...args);
    }

    // The effective status of the breaker only flips to 'open' if the minimum
    // volume threshold is met, the current request is rejected, _and_ the
    // opossum breaker is already open.
    if (breaker.opened) {
      setState('open')();
    }

    // isPassive allows the circuit breaker to be activated, to open and close, etc., but
    // prevents the breaker from rejecting any requests while it is open or
    // half-open. This allows for safe testing of the circuit breaker in
    // production environments, including the gathering of statistical
    // information regarding when the breaker would open.
    if (isPassive) {
      return fetchFunction(...args);
    }

    return Promise.reject(new Error(error));
  });

  // Report breaker status to interested consumers every statusInterval by
  // calling the onStatus function if provided.
  setInterval(() => {
    if (typeof options.onStatus === 'function') {
      options.onStatus({
        state,
      });
    }
  }, statusInterval || DEFAULT_STATUS_INTERVAL);

  return (...fetchArgs) => breaker.fire(...fetchArgs);
};

export default circuitBreaker;
